import { Mail } from "lucide-react";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEmailSettings, useSetEmailSettings } from "@/hooks/useEmailSettings";
import { ApiError } from "@/lib/api";
import { detectEnvironment } from "@/lib/environment";
import type { EmailArea } from "@/types/email-settings";

/**
 * A module settings-gear toggle (`ModuleDefinition.settingsToggles`): whether
 * `area`'s email really reaches internal staff in this lower environment.
 * Sandbox and development drop every outbound email, so without it the app
 * says Sent while nobody receives anything. Client contacts and every other
 * address stay guarded whatever it says. Declare it only outside production;
 * the server 404s it there, and this renders nothing on a 404. `label` names
 * the area's email when the module sends more than one kind.
 */
export function StaffEmailToggle({
  area,
  label = "Enable Emails",
}: {
  area: EmailArea;
  label?: string;
}) {
  const env = detectEnvironment().label;
  const { data, isLoading, error } = useEmailSettings(area);
  const save = useSetEmailSettings(area);
  const id = `staff-email-${area}`;

  if (error instanceof ApiError && error.status === 404) return null;

  const onToggle = (next: boolean) => {
    save.mutate(
      { send_to_staff_in_lower_envs: next },
      {
        onSuccess: () =>
          toast.success(next ? "Email to internal staff is on." : "Email to internal staff is off."),
        onError: (err) =>
          toast.error(
            err instanceof ApiError
              ? (err.asErrorBody?.message ?? "Update failed")
              : "Update failed",
          ),
      },
    );
  };

  const note = error
    ? "Couldn't load this setting."
    : data && data.sender == null
      ? `No email is configured in ${env}, so nothing is sent.`
      : `When on, email goes to staff at our own domains. Clients are never emailed from ${env}.`;

  return (
    <div className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm">
      <Mail size={16} strokeWidth={1.75} className="shrink-0 text-muted-foreground" />
      {/* The explanation lives in a tooltip so the popover stays one row per
          setting; it also says why the switch is disabled when it is. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <label htmlFor={id} className="flex-1 cursor-pointer text-foreground">
            {label}
          </label>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[240px]">
          {note}
        </TooltipContent>
      </Tooltip>
      {isLoading ? (
        <Skeleton className="h-5 w-9 shrink-0" />
      ) : (
        <Switch
          id={id}
          checked={data?.send_to_staff_in_lower_envs ?? false}
          disabled={!data || data.sender == null || save.isPending}
          onCheckedChange={onToggle}
          className="shrink-0"
        />
      )}
    </div>
  );
}
